import LightHelper from '@/commons/lights/LightHelper';
import { GuidettiWorld } from '@/guidetti/GuidettiWorld';
import ENV_PRESETS from '@/commons/environment/EnvironmentPreset';
import EnvironmentController from '@/commons/environment/EnvironmentController';
import { AmbientLight, SpotLight } from 'three';
import type IPreset from '@/commons/renderPresetToggleList/typescript/IPreset';
import type { TSunLightPreset } from '@/commons/renderPresetToggleList/typescript/TSunLightPreset';
import type { TShadowLightPreset } from '@/commons/renderPresetToggleList/typescript/TShadowLightPreset';
import type { TAmbientLightPreset } from '@/commons/renderPresetToggleList/typescript/TAmbientLightPreset';
import type { TColor } from '@/typescript/types/TColor';

function applyRenderPreset(preset: IPreset) {
    // SunLight
    applySunlightPreset(preset.sun, LightHelper.lightNames.sun);

    // ShadowLight
    applyShadowLightPreset(preset.shadow, LightHelper.lightNames.shadow);

    // AmbientLight
    applyAmbientLightPreset(preset.ambient, LightHelper.lightNames.ambient);

    // BackgroundColor
    applyBackgroundColor(preset.bgColor);

    // EnvMap
    const mapValueFromEnvPreset = ENV_PRESETS.find((envPreset) => envPreset.text === preset.envMap.name);
    if (mapValueFromEnvPreset) {
        EnvironmentController.useBackgroundAsEnvironment = preset.envMap.envAsBg;
        EnvironmentController.setEnvironmentMapPreset(mapValueFromEnvPreset.value);
    }
}

export function applySunlightPreset(sunLightParams: TSunLightPreset, name: string) {
    const { position, color, power, distance, shadow } = sunLightParams;
    // SunLight
    const sunLight = GuidettiWorld.scene?.getObjectByName(name) as SpotLight;
    if (sunLight) {
        const { x, y, z } = position;
        sunLight.position.set(x, y, z);

        const { r, g, b } = color; // as rgb value
        sunLight.color.r = r / 255;
        sunLight.color.g = g / 255;
        sunLight.color.b = b / 255;

        sunLight.power = power;
        sunLight.distance = distance;

        const { near, far } = shadow;
        sunLight.shadow.camera.near = near;
        sunLight.shadow.camera.far = far;
        sunLight.shadow.camera.updateProjectionMatrix();
        sunLight.shadow.needsUpdate = true;
    }
}

export function applyShadowLightPreset(shadowParams: TShadowLightPreset, name: string) {
    const { color, power } = shadowParams;
    const shadowSpot = GuidettiWorld.scene?.getObjectByName(name) as SpotLight;
    if (shadowSpot) {
        const { r, g, b } = color; // as rgb value
        shadowSpot.color.r = r / 255;
        shadowSpot.color.g = g / 255;
        shadowSpot.color.b = b / 255;

        shadowSpot.power = power;
    }
}

export function applyAmbientLightPreset(ambientParams: TAmbientLightPreset, name: string) {
    const { color, power } = ambientParams;
    const ambientLight = GuidettiWorld.scene?.getObjectByName(name) as AmbientLight;
    if (ambientLight) {
        const { r, g, b } = color;
        ambientLight.color.r = r / 255;
        ambientLight.color.g = g / 255;
        ambientLight.color.b = b / 255;
        ambientLight.intensity = power;
    }
}

export function applyBackgroundColor(color: TColor) {
    const { r, g, b } = color;
    EnvironmentController.setUseBackgroundAsEnvironment(false);
    EnvironmentController.backgroundColor.r = r / 255;
    EnvironmentController.backgroundColor.g = g / 255;
    EnvironmentController.backgroundColor.b = b / 255;
}

export default applyRenderPreset;
